// Import React Library
import React, { useRef, useState, useEffect } from "react";

// Import EmailJS SDK
import emailjs from "@emailjs/browser";

// Cloudinary Credentials
const CLOUD_NAME = "djl7xedoj";
const UPLOAD_PRESET = "prehouse";
const CLOUDINARY_FOLDER = "cv_postulaciones";

// PDF Endpoints
const CLOUDINARY_UPLOAD_URL = `https://api.cloudinary.com/v1_1/${CLOUD_NAME}/raw/upload`;

// EmailJS Credentials
const EMAILJS_SERVICE_ID = "service_7578jaz";
const EMAILJS_TEMPLATE_ID = "template_1osmhhi";
const EMAILJS_PUBLIC_KEY = "pxLmwp6Ex0LEGM1zq";

const MAX_FILE_MB = 15;
const telRegex = /^[0-9 +()\-]{7,20}$/;

// 🎨 Colores
const REQ_COLOR = "#fca5a5";   
const TITLE_COLOR = "#363636";  
const BRAND_YELLOW = "#FFBA00"; 
const LIGHT_GRAY_BG = "#f3f4f6"; 

const WorkWithUsComponent = () => {
  const formRef = useRef(null);
  const [uploadPct, setUploadPct] = useState(0);
  const [sending, setSending] = useState(false);

  const [statusMsg, setStatusMsg] = useState(null);
  const [selectedFileName, setSelectedFileName] = useState("");

  useEffect(() => {
    if (!statusMsg) return;
    const t = setTimeout(() => setStatusMsg(null), 5000);
    return () => clearTimeout(t);
  }, [statusMsg]);

  const validate = () => {
    const fd = new FormData(formRef.current);
    const nombre = String(fd.get("nombre") || "").trim();
    const apellido = String(fd.get("apellido") || "").trim();
    const telefono = String(fd.get("telefono") || "").trim();
    const file = formRef.current.cv?.files?.[0];

    if (!nombre) return "El nombre es obligatorio.";
    if (!apellido) return "El apellido es obligatorio.";
    if (!telefono) return "El teléfono es obligatorio.";
    if (!telRegex.test(telefono)) return "Teléfono inválido.";

    if (!file) return "Adjuntá tu CV en PDF.";
    if (file.type !== "application/pdf") return "El archivo debe ser PDF.";
    if (file.size > MAX_FILE_MB * 1024 * 1024) {
      return `El PDF no puede superar ${MAX_FILE_MB} MB. (Tu archivo: ${(
        file.size / 1024 / 1024
      ).toFixed(2)} MB)`;
    }
    return null;
  };

  const uploadToCloudinary = (file) => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("POST", CLOUDINARY_UPLOAD_URL);

      xhr.upload.addEventListener("progress", (e) => {
        if (e.lengthComputable) {
          const pct = Math.round((e.loaded * 100) / e.total);
          setUploadPct(pct);
        }
      });

      xhr.onreadystatechange = () => {
        if (xhr.readyState === 4) {
          try {
            const res = JSON.parse(xhr.responseText || "{}");
            if (xhr.status >= 200 && xhr.status < 300 && res.secure_url) {
              resolve(res.secure_url);
            } else {
              reject(
                new Error(
                  res.error?.message ||
                    `Error subiendo a Cloudinary (status ${xhr.status})`
                )
              );
            }
          } catch (err) {
            reject(new Error("Respuesta inválida de Cloudinary"));
          }
        }
      };

      const data = new FormData();
      data.append("file", file);
      data.append("upload_preset", UPLOAD_PRESET);
      if (CLOUDINARY_FOLDER) data.append("folder", CLOUDINARY_FOLDER);
      data.append(
        "public_id",
        `${Date.now()}_${file.name.replace(/\s+/g, "_")}`
      );
      xhr.send(data);
    });
  };

  const sendEmail = async ({ nombre, apellido, telefono, cv_url }) => {
    return emailjs.send(
      EMAILJS_SERVICE_ID,
      EMAILJS_TEMPLATE_ID,
      { nombre, apellido, telefono, cv_url },
      { publicKey: EMAILJS_PUBLIC_KEY }
    );
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatusMsg(null);
    setUploadPct(0);

    // Anti-bots (honeypot)
    const hp = new FormData(formRef.current).get("empresa");
    if (hp) return; // si se completa este campo oculto, no hacemos nada

    const err = validate();
    if (err) {
      setStatusMsg({ type: "error", text: err });
      return;
    }

    const fd = new FormData(formRef.current);
    const file = formRef.current.cv.files[0];

    try {
      setSending(true);

      // 1) Subir PDF a Cloudinary
      const cvUrl = await uploadToCloudinary(file);

      // 2) Enviar email con el link del CV
      await sendEmail({
        nombre: fd.get("nombre"),
        apellido: fd.get("apellido"),
        telefono: fd.get("telefono"),
        cv_url: cvUrl,
      });

      setStatusMsg({
        type: "success",
        text: "¡Postulación enviada! Revisaremos tu CV a la brevedad.",
      });

      // Reset del formulario y estados
      formRef.current.reset();
      setSelectedFileName("");
      setUploadPct(0);
    } catch (error) {
      console.error(error);
      setStatusMsg({
        type: "error",
        text:
          error?.message ||
          "No se pudo completar el envío. Probá nuevamente en unos minutos.",
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <section
      aria-labelledby="titulo-trabaja"
      style={{
        maxWidth: 560,
        padding: 16,
        borderRadius: 12,
        border: "1px solid #e5e7eb",
        boxShadow: "0 1px 8px rgba(0,0,0,0.06)",
        margin: "50px auto",
        background: "white",
      }}
    >
      <h2
        id="titulo-trabaja"
        style={{
          marginBottom: 8,
          textTransform: "uppercase",
          color: TITLE_COLOR,
          letterSpacing: "0.02em",
          fontWeight: "bold",
          textAlign: "center",
        }}
      >
        Dejanos tu CV
      </h2>
      <span
        aria-hidden="true"
        style={{
          display: "block",
          width: 50,
          height: 5,
          background: BRAND_YELLOW,
          borderRadius: 1,
          margin: "25px auto",
        }}
      />

      <p style={{ margin: "0 0 16px 0", color: "#4b5563" }}>
        Completá tus datos y adjuntá tu CV (PDF). Subiremos tu archivo de forma
        segura y nos llegará un email con el enlace de descarga.
      </p>

      <form ref={formRef} onSubmit={onSubmit} noValidate>
        {/* Nombre */}
        <div style={{ marginBottom: 12 }}>
          <label htmlFor="nombre" style={{ display: "block", marginBottom: 6 }}>
            Nombre
            <span aria-hidden="true" style={{ color: REQ_COLOR }}> *</span>
          </label>
          <input
            id="nombre"
            name="nombre"
            type="text"
            placeholder="Juan"
            required
            autoComplete="given-name"
            style={{
              width: "100%",
              padding: "10px 12px",
              borderRadius: 8,
              border: "1px solid #d1d5db",
            }}
          />
        </div>

        {/* Apellido */}
        <div style={{ marginBottom: 12 }}>
          <label htmlFor="apellido" style={{ display: "block", marginBottom: 6 }}>
            Apellido
            <span aria-hidden="true" style={{ color: REQ_COLOR }}> *</span>
          </label>
          <input
            id="apellido"
            name="apellido"
            type="text"
            placeholder="Pérez"
            required
            autoComplete="family-name"
            style={{
              width: "100%",
              padding: "10px 12px",
              borderRadius: 8,
              border: "1px solid #d1d5db",
            }}
          />
        </div>

        {/* Teléfono */}
        <div style={{ marginBottom: 12 }}>
          <label htmlFor="telefono" style={{ display: "block", marginBottom: 6 }}>
            Teléfono
            <span aria-hidden="true" style={{ color: REQ_COLOR }}> *</span>
          </label>
          <input
            id="telefono"
            name="telefono"
            type="tel"
            placeholder="+598 9 123 4567"
            required
            autoComplete="tel"
            inputMode="tel"
            style={{
              width: "100%",
              padding: "10px 12px",
              borderRadius: 8,
              border: "1px solid #d1d5db",
            }}
          />
          <small style={{ color: "#6b7280" }}>
            Permitidos dígitos, espacios, +, (), - (7 a 20 caracteres).
          </small>
        </div>

        <div style={{ marginBottom: 16 }}>
          <label htmlFor="cv" style={{ display: "block", marginBottom: 6 }}>
            CV (PDF)
            <span aria-hidden="true" style={{ color: REQ_COLOR }}> *</span>
          </label>

          <input
            id="cv"
            name="cv"
            type="file"
            accept=".pdf,application/pdf"
            required
            onChange={(e) => {
              const file = e.target.files?.[0];
              setSelectedFileName(file ? file.name : "");
            }}
            style={{
              position: "absolute",
              width: 1,
              height: 1,
              padding: 0,
              margin: -1,
              overflow: "hidden",
              clip: "rect(0,0,0,0)",
              whiteSpace: "nowrap",
              border: 0,
            }}
          />

          <label
            htmlFor="cv"
            className="upload-trigger"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              padding: "10px 14px",
              borderRadius: 8,
              border: "1px solid #d1d5db",
              background: LIGHT_GRAY_BG,  
              color: "#111827",
              cursor: "pointer",
              fontWeight: 600,
              userSelect: "none",
            }}
            role="button"
            tabIndex={0}
          >
            {/* Ícono Upload (SVG) */}
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="17 8 12 3 7 8"/>
              <line x1="12" y1="3" x2="12" y2="15"/>
            </svg>
            Subir PDF
          </label>

          {/* Nombre del archivo seleccionado */}
          {selectedFileName ? (
            <div style={{ marginTop: 8, color: "#374151" }}>
              Archivo seleccionado: <strong>{selectedFileName}</strong>
            </div>
          ) : (
            <div style={{ marginTop: 8, color: "#6b7280" }}>
              No hay archivo seleccionado.
            </div>
          )}

          <small style={{ color: "#6b7280", display: "block", marginTop: 6 }}>
            Solo PDF. Tamaño máximo recomendado: {MAX_FILE_MB} MB.
          </small>
        </div>

        {/* Barra de progreso al subir */}
        {sending && (
          <div style={{ marginBottom: 10 }}>
            <div
              aria-label="Progreso de subida"
              style={{
                height: 10,
                background: "#e5e7eb",
                borderRadius: 999,
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  width: `${uploadPct}%`,
                  height: "100%",
                  background: "#3b82f6",
                  transition: "width 0.2s ease",
                }}
              />
            </div>
            <small style={{ color: "#6b7280" }}>{uploadPct}%</small>
          </div>
        )}

        {/* Mensajes de estado (se ocultan a los 5 segundos gracias al useEffect) */}
        {statusMsg && (
          <div
            role="alert"
            style={{
              marginBottom: 12,
              padding: "10px 12px",
              borderRadius: 8,
              background:
                statusMsg.type === "success" ? "#ecfdf5" : "#fef2f2",
              color: statusMsg.type === "success" ? "#065f46" : "#991b1b",
              border:
                statusMsg.type === "success"
                  ? "1px solid #a7f3d0"
                  : "1px solid #fecaca",
            }}
          >
            {statusMsg.text}
          </div>
        )}

        {/* Botón enviar */}
        <button
          type="submit"
          disabled={sending}
          style={{
            width: "100%",
            padding: "12px 16px",
            borderRadius: 8,
            border: "none",
            color: "white",
            cursor: sending ? "not-allowed" : "pointer",
            fontWeight: 600,
          }}
          className="bg-gray-800 text-white hover:bg-gray-600"
        >
          {sending ? "Enviando..." : "Enviar postulación"}
        </button>

        {/* Honeypot anti-bots */}
        <input
          type="text"
          name="empresa"
          style={{ display: "none" }}
          tabIndex={-1}
          autoComplete="off"
        />
      </form>
    </section>
  );
};

export default WorkWithUsComponent;